import { Building2, Plus, ShoppingCart, Users } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'

import { DirectMessageModal } from '@/modules/messages/components/DirectMessageModal'
import { useMessages } from '@/modules/messages/hooks/useMessages'
import { AddVendorModal } from '@/modules/purchase/components/AddVendorModal'
import { CreatePurchaseOrderModal } from '@/modules/purchase/components/CreatePurchaseOrderModal'
import { PurchaseOrderTable } from '@/modules/purchase/components/PurchaseOrderTable'
import { VendorTable } from '@/modules/purchase/components/VendorTable'
import { usePurchaseOrderStore } from '@/modules/purchase/store/purchaseOrder.store'
import { useVendorStore } from '@/modules/purchase/store/vendor.store'
import { useCommentsStore } from '@/modules/sales/stores/comments.store'
import { Button } from '@/shared/components/ui/button'
import { ConfirmDialog } from '@/shared/components/ui/ConfirmDialog'

import { AddPurchaseDialog } from '../components/purchase/AddPurchaseDialog'
import { PurchaseTable } from '../components/purchase/PurchaseTable'
import { usePurchase } from '../hooks/usePurchase'

export default function AdminPurchaseRoute() {
  const [activeTab, setActiveTab] = useState('orders') // 'orders' | 'team' | 'vendors'
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [selectedPurchase, setSelectedPurchase] = useState(null)

  const [confirmOpen, setConfirmOpen] = useState(false)
  const [purchaseToDelete, setPurchaseToDelete] = useState(null)

  // Direct Messaging State
  const [messageModalOpen, setMessageModalOpen] = useState(false)
  const [messagePartner, setMessagePartner] = useState(null)

  const { deletePurchasePerson } = usePurchase()

  // Purchase Orders store for Admin
  const {
    purchaseOrders,
    isLoading: isLoadingOrders,
    isSubmitting: isSubmittingOrder,
    fetchPurchaseOrders,
    createPurchaseOrder,
    fetchNextPurchaseOrderNumber,
  } = usePurchaseOrderStore()
  const [orderSearch, setOrderSearch] = useState('')

  // Vendors store for Admin
  const {
    vendors,
    isLoading: isLoadingVendors,
    isSubmitting: isSubmittingVendor,
    fetchVendors,
    createVendor,
    updateVendor,
    deleteVendor,
  } = useVendorStore()
  const [vendorSearch, setVendorSearch] = useState('')

  // Vendor Modals State
  const [isVendorModalOpen, setIsVendorModalOpen] = useState(false)
  const [editingVendor, setEditingVendor] = useState(null)
  const [vendorToDelete, setVendorToDelete] = useState(null)

  // PO Modal State
  const [isPurchaseOrderModalOpen, setIsPurchaseOrderModalOpen] =
    useState(false)
  const [selectedPoVendor, setSelectedPoVendor] = useState(null)

  const { notifications } = useCommentsStore()
  const { unreadCounts } = useMessages()

  // Calculate unread purchase order comments
  const hasUnreadPurchaseComments = useMemo(() => {
    return notifications.some(
      (n) => (n.purchaseOrderId || n.purchaseOrder) && !n.isRead,
    )
  }, [notifications])

  // Calculate unread purchase DMs
  const hasUnreadPurchaseDMs = (unreadCounts?.byRole?.purchase || 0) > 0

  useEffect(() => {
    fetchPurchaseOrders().catch(() => {})
    fetchVendors().catch(() => {})
  }, [fetchPurchaseOrders, fetchVendors])

  const filteredPurchaseOrders = useMemo(() => {
    const query = orderSearch.trim().toLowerCase()
    if (!query) return purchaseOrders

    return purchaseOrders.filter((order) =>
      [
        order.purchaseOrderNumber,
        order.vendor?.vendorName,
        order.paymentTerms,
        order.deliveryTerms,
        order.purchaseOrderDate,
      ]
        .filter(Boolean)
        .some((val) => String(val).toLowerCase().includes(query)),
    )
  }, [purchaseOrders, orderSearch])

  const filteredVendors = useMemo(() => {
    const query = vendorSearch.trim().toLowerCase()
    if (!query) return vendors

    const matching = vendors.filter((vendor) => {
      const name = String(
        vendor.vendorName || vendor.companyName || '',
      ).toLowerCase()
      return name.includes(query)
    })

    return matching.sort((a, b) => {
      const nameA = String(a.vendorName || a.companyName || '').toLowerCase()
      const nameB = String(b.vendorName || b.companyName || '').toLowerCase()
      const startsWithA = nameA.startsWith(query)
      const startsWithB = nameB.startsWith(query)

      if (startsWithA && !startsWithB) return -1
      if (!startsWithA && startsWithB) return 1
      return nameA.localeCompare(nameB)
    })
  }, [vendors, vendorSearch])

  // Purchase Team handlers
  const handleAdd = () => {
    setSelectedPurchase(null)
    setIsDialogOpen(true)
  }

  const handleEdit = (purchase) => {
    setSelectedPurchase(purchase)
    setIsDialogOpen(true)
  }

  const handleDelete = (purchase) => {
    setPurchaseToDelete(purchase)
    setConfirmOpen(true)
  }

  const handleMessage = (purchase) => {
    setMessagePartner(purchase)
    setMessageModalOpen(true)
  }

  const confirmDelete = async () => {
    if (!purchaseToDelete) return

    try {
      await deletePurchasePerson(purchaseToDelete._id || purchaseToDelete.id)
      setConfirmOpen(false)
      setPurchaseToDelete(null)
    } catch {
      // Store already shows error toast
    }
  }

  // PO handlers
  const handleOpenCreatePoModal = (vendor = null) => {
    setSelectedPoVendor(vendor)
    setIsPurchaseOrderModalOpen(true)
  }

  const handleCloseCreatePoModal = () => {
    if (!isSubmittingOrder) {
      setIsPurchaseOrderModalOpen(false)
      setSelectedPoVendor(null)
    }
  }

  const handleCreatePurchaseOrder = async (poPayload) => {
    try {
      const result = await createPurchaseOrder(poPayload)
      if (result) {
        setIsPurchaseOrderModalOpen(false)
        setSelectedPoVendor(null)
        setActiveTab('orders')
      }
    } catch {
      // Store displays error toast
    }
  }

  // Vendor handlers
  const handleOpenAddVendorModal = () => {
    setEditingVendor(null)
    setIsVendorModalOpen(true)
  }

  const handleEditVendor = (vendor) => {
    setEditingVendor(vendor)
    setIsVendorModalOpen(true)
  }

  const handleDeleteVendor = (vendor) => {
    setVendorToDelete(vendor)
  }

  const confirmDeleteVendor = async () => {
    if (!vendorToDelete) return

    try {
      await deleteVendor(vendorToDelete._id)
      setVendorToDelete(null)
    } catch {
      // Store displays error toast
    }
  }

  const handleCreateVendor = async (vendorPayload) => {
    try {
      const result = await createVendor(vendorPayload)
      if (result) {
        setIsVendorModalOpen(false)
        return true
      }
    } catch {
      // Store displays error toast
    }
    return false
  }

  const handleUpdateVendor = async (vendorPayload) => {
    if (!editingVendor) return false

    try {
      const result = await updateVendor(editingVendor._id, vendorPayload)
      if (result) {
        setIsVendorModalOpen(false)
        setEditingVendor(null)
        return true
      }
    } catch {
      // Store displays error toast
    }
    return false
  }

  return (
    <div className="space-y-6">
      {/* Navigation Tabs */}
      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={() => setActiveTab('orders')}
          className={`relative flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition ${
            activeTab === 'orders'
              ? 'bg-[#080d25] text-white shadow-sm'
              : 'border border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
          }`}
        >
          <ShoppingCart className="size-4" />
          <span>Purchase Orders</span>

          {hasUnreadPurchaseComments && (
            <span className="absolute right-2 top-2 size-1.5 rounded-full bg-red-500 ring-2 ring-white" />
          )}
        </button>

        <button
          type="button"
          onClick={() => setActiveTab('team')}
          className={`relative flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition ${
            activeTab === 'team'
              ? 'bg-[#080d25] text-white shadow-sm'
              : 'border border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
          }`}
        >
          <Users className="size-4" />
          <span>Purchase Team</span>
          {hasUnreadPurchaseDMs && (
            <span className="absolute right-2 top-2 size-2.5 rounded-full bg-red-500 ring-2 ring-white" />
          )}
        </button>

        <button
          type="button"
          onClick={() => setActiveTab('vendors')}
          className={`relative flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition ${
            activeTab === 'vendors'
              ? 'bg-[#080d25] text-white shadow-sm'
              : 'border border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
          }`}
        >
          <Building2 className="size-4" />
          <span>Vendor List</span>
        </button>
      </div>

      {activeTab === 'orders' && (
        <PurchaseOrderTable
          purchaseOrders={purchaseOrders}
          filteredOrders={filteredPurchaseOrders}
          search={orderSearch}
          onSearchChange={setOrderSearch}
          isLoading={isLoadingOrders}
          onOpenCreatePoModal={() => handleOpenCreatePoModal(null)}
          isAdmin={false}
        />
      )}

      {activeTab === 'team' && (
        <>
          <div className="flex flex-wrap items-center justify-between gap-6 rounded-3xl border border-gray-200 bg-white p-5 shadow-sm">
            <div>
              <h1 className="text-3xl font-semibold tracking-tight text-slate-950">
                Purchase
              </h1>

              <p className="mt-2 text-lg text-slate-500">
                Manage Purchase users.
              </p>
            </div>

            <Button
              className="h-12 rounded-full bg-slate-950 px-4 text-base text-white hover:bg-gray-900"
              onClick={handleAdd}
            >
              <Plus className="mr-2 h-5 w-5" />
              Add Purchase Person
            </Button>
          </div>

          <PurchaseTable
            onEdit={handleEdit}
            onDelete={handleDelete}
            onMessage={handleMessage}
          />
        </>
      )}

      {activeTab === 'vendors' && (
        <VendorTable
          vendors={vendors}
          filteredVendors={filteredVendors}
          search={vendorSearch}
          onSearchChange={setVendorSearch}
          isLoading={isLoadingVendors}
          onOpenAddVendorModal={handleOpenAddVendorModal}
          onCreatePoForVendor={(vendor) => handleOpenCreatePoModal(vendor)}
          onEditVendor={handleEditVendor}
          onDeleteVendor={handleDeleteVendor}
          isAdmin={false}
        />
      )}

      {/* Add / Edit Purchase Person Dialog */}
      <AddPurchaseDialog
        open={isDialogOpen}
        onOpenChange={(open) => {
          setIsDialogOpen(open)
          if (!open) setSelectedPurchase(null)
        }}
        purchase={selectedPurchase}
      />

      {/* Add / Edit Vendor Modal */}
      <AddVendorModal
        isOpen={isVendorModalOpen}
        onClose={() => {
          if (!isSubmittingVendor) {
            setIsVendorModalOpen(false)
            setEditingVendor(null)
          }
        }}
        initialVendor={editingVendor}
        onSubmit={editingVendor ? handleUpdateVendor : handleCreateVendor}
        isSubmitting={isSubmittingVendor}
      />

      {/* Create Purchase Order Modal */}
      <CreatePurchaseOrderModal
        isOpen={isPurchaseOrderModalOpen}
        onClose={handleCloseCreatePoModal}
        initialVendor={selectedPoVendor}
        vendors={vendors}
        purchaseOrders={purchaseOrders}
        onSubmit={handleCreatePurchaseOrder}
        isSubmitting={isSubmittingOrder}
        fetchNextNumber={fetchNextPurchaseOrderNumber}
      />

      {/* Confirm Delete Purchase Person */}
      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Delete Purchase Person"
        description={`Are you sure you want to delete "${purchaseToDelete?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={confirmDelete}
      />

      {/* Confirm Delete Vendor */}
      <ConfirmDialog
        open={Boolean(vendorToDelete)}
        onOpenChange={(open) => {
          if (!open) setVendorToDelete(null)
        }}
        title="Delete Vendor"
        description={`Are you sure you want to delete "${vendorToDelete?.vendorName}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        loading={isSubmittingVendor}
        onConfirm={confirmDeleteVendor}
      />

      {/* Direct Messaging */}
      <DirectMessageModal
        open={messageModalOpen}
        onOpenChange={setMessageModalOpen}
        partner={messagePartner}
      />
    </div>
  )
}
